import { X, Minus, Plus, ShoppingBag, Trash2, CreditCard, ArrowRight, Store, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useCarrito } from './useCarrito.ts';
import { formatCurrency } from '../../lib/format.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../lib/dolar.ts';
import { api } from '@/lib/api.ts';
import { useAuth } from '../auth/useAuth.ts';

// --- COMPONENTE DE ITEM INDIVIDUAL ---
function CartItemRow({ item, storeSlug, updateQuantity, removeItem }) {

  const productName = item.name || item.product_name || 'Producto sin nombre';
  const productImage = item.image || item.product_image;

  return (
    <div className="flex gap-4 group animate-in slide-in-from-right-4 duration-300 mb-4">
      {/* Imagen */}
      <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl border border-white/10 bg-white/5">
        <img
          src={productImage ?? 'https://placehold.co/100x100?text=Product'}
          alt={productName}
          className="h-full w-full object-cover object-center"
          onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=Product'; }}
        />
      </div>

      {/* Info */}
      <div className="flex flex-1 flex-col justify-between py-1">
        <div>
          <div className="flex justify-between text-base font-medium text-white">
            <h3 className="line-clamp-2 mr-2 text-sm leading-tight text-white/90">{productName}</h3>
            <p className="tabular-nums tracking-tight text-sm font-light">{formatCurrency(item.price * item.quantity)}</p>
          </div>
          {item.variant_attributes && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {Object.entries(item.variant_attributes).map(([key, val]) => (
                <span key={key} className="text-[9px] text-white/50 uppercase bg-white/5 px-1.5 py-0.5 rounded border border-white/5">
                  {val}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Controles */}
        <div className="flex items-end justify-between mt-2">
          {updateQuantity ? (
            <div className="flex items-center bg-[#151515] rounded-lg border border-white/10 h-7">
              <button onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1), storeSlug)} className="w-7 h-full flex items-center justify-center hover:bg-white/10 text-white/50 hover:text-white"><Minus className="h-3 w-3" /></button>
              <span className="w-6 text-center text-xs font-mono text-white">{item.quantity}</span>
              <button onClick={() => updateQuantity(item.id, item.quantity + 1, storeSlug)} className="w-7 h-full flex items-center justify-center hover:bg-white/10 text-white/50 hover:text-white"><Plus className="h-3 w-3" /></button>
            </div>
          ) : (
            <div className="flex items-center h-7 px-2 bg-white/5 rounded-lg text-[10px] font-mono text-white/60 border border-white/5">
              Cant: {item.quantity}
            </div>
          )}

          {removeItem && (
            <button
              onClick={() => removeItem(item.id, storeSlug)}
              className="text-indigo-400/60 hover:text-rose-400 p-1.5 hover:bg-white/5 rounded-lg transition-colors"
              title="Eliminar producto"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- COMPONENTE ACORDEÓN ---
function StoreAccordion({ store, isCurrentStore, itemsToRender, total, itemCount, pricingRate, onCheckout, onNavigate, onRemoveRemote }) {
  const [isExpanded, setIsExpanded] = useState(isCurrentStore);
  const { updateQuantity, removeItem } = useCarrito();

  const finalUpdate = isCurrentStore ? updateQuantity : null;
  const finalRemove = isCurrentStore ? removeItem : onRemoveRemote;

  const dualTotal = (isCurrentStore && pricingRate) ? formatDualPrice(total, pricingRate) : null;

  if (itemCount === 0 && isCurrentStore) return null;

  return (
    <div className={`border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 mb-4 ${isExpanded ? 'bg-[#0F0F0F]' : 'bg-[#0B0B0B] hover:border-white/20'}`}>
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isCurrentStore ? 'bg-indigo-500 text-white' : 'bg-white/10 text-white/50'}`}>
            <Store className="h-4 w-4" />
          </div>
          <div>
            <h3 className={`text-sm font-bold uppercase tracking-wide ${isCurrentStore ? 'text-white' : 'text-white/70'}`}>
              {isCurrentStore ? 'Tienda Actual' : store.store_name}
            </h3>
            <p className="text-[10px] text-white/40">{itemCount} Productos • {formatCurrency(total)}</p>
          </div>
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
      </button>

      {isExpanded && (
        <div className="border-t border-white/5 p-4 bg-black/20">
          <div className="mb-4">
            {itemsToRender?.map((item) => (
              <CartItemRow
                key={item.id}
                item={item}
                storeSlug={isCurrentStore ? store.slug : store.store_slug}
                updateQuantity={finalUpdate}
                removeItem={finalRemove}
              />
            ))}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-white/5">
            <div className="text-right">
              <p className="text-lg font-light text-white">{formatCurrency(total)}</p>
              {dualTotal && <p className="text-[10px] text-emerald-500 font-mono">{dualTotal.local}</p>}
            </div>

            {isCurrentStore ? (
              <button onClick={onCheckout} className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors">
                Pagar <CreditCard className="h-3 w-3" />
              </button>
            ) : (
              <button onClick={() => onNavigate(store.store_slug)} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-indigo-500 transition-colors">
                Ir a Pagar <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function CartDrawer() {
  const { items, isOpen, closeCart } = useCarrito();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: pricingRate } = usePublicStorePricingRate(slug);

  const queryClient = useQueryClient();

  // 1. FILTRADO INTELIGENTE
  const currentStoreItems = items.filter(item => item.storeSlug === slug || !item.storeSlug);
  const currentTotal = currentStoreItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const hasCurrentItems = currentStoreItems.length > 0;

  // 2. CONSULTA DE OTROS CARRITOS
  const { data: globalCarts } = useQuery({
    queryKey: ['global-carts-drawer', slug],
    queryFn: async () => {
      if (!isAuthenticated) return [];
      try {
        const { data } = await api.get('/api/carrito/manage/global/');

        // TRANSFORMACIÓN DE ID CRÍTICA:
        // Convertimos el ID de base de datos a "ProductoID-VarianteID" para que coincida con lo que espera el backend al borrar.
        return Array.isArray(data) ? data.map(cart => ({
          ...cart,
          items: cart.items.map(item => ({
            ...item,
            // ID Híbrido para compatibilidad con removeItem
            id: item.variant ? `${item.product}-${item.variant}` : String(item.product)
          }))
        })) : [];
      } catch (e) { return []; }
    },
    enabled: isOpen && isAuthenticated,
  });

  const otherCarts = globalCarts?.filter(c => c.store_slug !== slug && c.total_items > 0) || [];
  const hasOtherCarts = otherCarts.length > 0;

  const handleCheckout = () => {
    closeCart();
    navigate(`/t/${slug}/carrito`);
  };

  const handleNavigateStore = (targetSlug) => {
    window.location.href = `/t/${targetSlug}/carrito`;
  };

  // --- FUNCIÓN DE BORRADO REMOTO ---
  const handleRemoveRemoteItem = async (itemId, storeSlug) => {
    console.log(`Borrando item remoto: ${itemId} de tienda: ${storeSlug}`);
    try {
      await api.post('/api/carrito/manage/remove_item/', {
        item_id: itemId,
        store_slug: storeSlug
      });
      // Refrescamos TODO para que desaparezca visualmente
      await queryClient.invalidateQueries({ queryKey: ['global-carts-drawer'] });
      await queryClient.invalidateQueries({ queryKey: ['global-cart-count-nav'] }); // También el badge del navbar
    } catch (error) {
      console.error("Error borrando item remoto:", error);
    }
  };

  const isTotallyEmpty = !hasCurrentItems && !hasOtherCarts;

  return (
    <div className={`fixed inset-0 z-[60] flex justify-end ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={closeCart} />

      <div className={`relative w-screen max-w-md bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col h-full transform transition-transform duration-500 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        <div className="flex items-center justify-between px-6 py-6 border-b border-white/5 bg-[#0F0F0F]">
          <h2 className="text-lg font-bold text-white uppercase tracking-widest flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 text-indigo-500" />
            Mis Compras
          </h2>
          <button onClick={closeCart} className="p-2 -mr-2 text-white/40 hover:text-white rounded-full hover:bg-white/5"><X className="h-6 w-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">

          {isTotallyEmpty && (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center">
                <ShoppingBag className="h-8 w-8 text-white/20" />
              </div>
              <p className="text-sm font-light text-white/60">Tu carrito está vacío.</p>
              <button onClick={closeCart} className="text-xs font-bold text-indigo-400 uppercase border-b border-indigo-500/30 hover:border-indigo-500 pb-0.5 transition-all">
                Seguir comprando
              </button>
            </div>
          )}

          <div className="space-y-6">
            {hasCurrentItems && (
              <StoreAccordion
                store={{ slug, store_name: 'Tienda Actual' }}
                isCurrentStore={true}
                itemsToRender={currentStoreItems}
                total={currentTotal}
                itemCount={currentStoreItems.length}
                pricingRate={pricingRate}
                onCheckout={handleCheckout}
              />
            )}

            {hasOtherCarts && (
              <>
                {hasCurrentItems && (
                  <div className="flex items-center gap-2 mb-4 px-1">
                    <div className="h-px flex-1 bg-white/10"></div>
                    <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Otras Tiendas</span>
                    <div className="h-px flex-1 bg-white/10"></div>
                  </div>
                )}

                {otherCarts.map(cart => (
                  <StoreAccordion
                    key={cart.id}
                    store={cart}
                    isCurrentStore={false}
                    itemsToRender={cart.items}
                    total={parseFloat(cart.total_price)}
                    itemCount={cart.total_items}
                    onNavigate={handleNavigateStore}
                    onRemoveRemote={handleRemoveRemoteItem}
                  />
                ))}
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}